import set from "lodash/set";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import DropdownOptions from "../../../../parts/dropdownOptions";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  getCommunityChatConversations,
  getConversationLabel,
  getConversationLabelColor,
  getFilteredConversations,
  modifyConversation,
  sortByMostRecent,
  sortConversationsByName,
} from "../../../../utils/chat-util";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { truncateText } from "../../../../utils/string-helpers";
import { SettingsCommunityChatOptions } from "./SettingsCommunityChatOptions";

export const CommunityChatList = React.memo(
  ({
    locals,
    api,
    chatbox,
    conversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    setMobileChatOpened,
  }) => {
    const { t } = useTranslation("coaching-portal");
    const { activeUser, activeUserToRequest, flash, query } = locals;
    const { _id: userId, webAppTheme = "dark" } = activeUser;
    const {
      pushAccountInfo = {},
      pressAccountInfo = {},
      performSettings,
      pullAccountInfo,
    } = activeUserToRequest;
    const pushEnabled = pushAccountInfo?.inAppCommunityEnabled;
    const pressEnabled = pressAccountInfo?.inAppCommunityEnabled;
    const performEnabled = performSettings?.inAppCommunityEnabled;
    const pullEnabled = pullAccountInfo?.inAppCommunityEnabled;

    const enabledCommunityTypes = [
      pushEnabled,
      pressEnabled,
      performEnabled,
      pullEnabled,
    ].filter(Boolean);
    const hasMultipleCommunities = enabledCommunityTypes.length > 1;
    const singleCommunityType = !hasMultipleCommunities
      ? pushEnabled
        ? "push"
        : pressEnabled
          ? "press"
          : performEnabled
            ? "perform"
            : pullEnabled
              ? "pull"
              : null
      : null;

    const labels = {
      push: t("communityChat.labelSubscribers"),
      press: t("communityChat.labelGymMembers"),
      perform: t("communityChat.labelGymMembers"),
      pull: t("communityChat.labelClients"),
    };
    const { directMessagesEnabled = true } = pushAccountInfo;
    const {
      readChannels = [],
      readDirects = [],
      unreadChats = [],
    } = conversations;
    const { modal: { type: modalType } = {} } = query;
    set(locals, "allCommunityConversations", conversations);

    const newDropdownOptions = [
      {
        label: t("communityChat.addNewChannel"),
        value: "new-community-channel",
      },
      { label: t("communityChat.newMessage"), value: "new-community-message" },
    ];

    const initialDropwdownState = {
      newDropdown: false,
      channelDropdown: "",
      messageDropdown: "",
    };

    const chatRefs = useRef({}); //Refs to store the reference of the chats
    const newButtonRef = useRef(null); // referring the "new" button in the nav
    const prevModalType = useRef(); //this is to control a call when the user closes the edit channel/new message/new channel modals
    const [searchValue, setSearchValue] = useState(""); //this is to filter the values in the "search input"
    const [dropdownState, setDropdownState] = useState(initialDropwdownState); //this is to show the dropdowns
    const [dropdownPosition, setDropdownPosition] = useState(0);
    const [displayState, setDisplayState] = useState("show-all"); //this is to control the options to display in the left bat
    const { newDropdown, channelDropdown, messageDropdown } = dropdownState;

    //We are looking if the user filtered chats by words
    const filteredReadChannels = getFilteredConversations(
      searchValue,
      readChannels
    );
    const filteredReadDirects = getFilteredConversations(
      searchValue,
      readDirects
    );
    const filteredUnreadChats = getFilteredConversations(
      searchValue,
      unreadChats
    );

    /**
     * Options to show in the dropdown of the conversations
     * @param {*} type
     * @param {*} ableToNotify
     * @returns
     */
    const getDropdownOptions = (type, ableToNotify) => {
      if (type === "channel") {
        return [
          {
            label: ableToNotify
              ? t("communityChat.muteChannel")
              : t("communityChat.unmuteChannel"),
            value: ableToNotify ? "mute-conversation" : "unmute-conversation",
          },
          {
            label: t("communityChat.editChannel"),
            value: "edit-community-channel",
          },
          {
            label: t("communityChat.deleteChannel"),
            value: "delete-conversation",
          },
        ];
      } else if (type === "conversation") {
        return [
          {
            label: ableToNotify
              ? t("communityChat.muteConversation")
              : t("communityChat.unmuteConversation"),
            value: ableToNotify ? "mute-conversation" : "unmute-conversation",
          },
          {
            label: t("communityChat.deleteConversation"),
            value: "delete-conversation",
          },
        ];
      }
    };

    /**
     * Open the edit channel modal
     * @param {*} value
     * @param {*} conversationId
     */
    const handleOpenModal = (value, conversationId) => {
      if (value) {
        setDropdownState(initialDropwdownState);
        submit(`/actions/refresh/?`, {
          method: "GET",
          body: {
            "modal[type]": value,
            conversationId,
          },
        });
      }
    };

    /**
     * Function to open the edit channel modal, or mute/unmute/delete conversation
     * @param {*} option
     * @param {*} conversation
     */
    const handleConversationAction = async (option, conversation) => {
      setDropdownState(initialDropwdownState);
      const { id, custom } = conversation;
      if (option === "edit-community-channel") {
        handleOpenModal(option, id);
      } else {
        try {
          const body = {
            option,
            conversationId: id,
            custom,
          };

          await modifyConversation(api, body);

          const data = await getCommunityChatConversations(api, userId);
          setConversations(data);

          flash("success", t("communityChat.conversationUpdated"));
        } catch (e) {
          flash("error", e.message);
        }
        submit(`/actions/community-chat/refresh`, {
          method: "POST",
        });
      }
    };

    /**
     * Open the dropdowns
     * @param {*} e
     * @param {*} state
     * @param {*} value
     */
    const openDropdown = (e, state, value) => {
      e.stopPropagation();
      e.preventDefault();
      setDropdownState({
        ...dropdownState,
        [state]: value,
      });
    };

    /**
     * Select a new conversation
     * @param {*} id
     */
    const handleSelectConversation = async (id) => {
      setSelectedConversation(id);
      chatbox.current.select(id);

      //Here we are checking if we are selecting an unread conversation
      const newUnreadChats = [...unreadChats];
      const conversationIndex = newUnreadChats.findIndex(
        (chat) => chat.id === id
      );

      //If the conversation isn't readed we need to update the state to move it to the read conversations array
      if (conversationIndex !== -1) {
        const conversationFound = newUnreadChats[conversationIndex];

        // If the conversation has subject we are going to move it to the read channels
        if (conversationFound.subject) {
          setConversations({
            readChannels: [...readChannels, conversationFound],
            readDirects,
            unreadChats: newUnreadChats.filter(
              (_, index) => index !== conversationIndex
            ),
          });
        } else {
          //if not, we are going to move it to the read directs
          setConversations({
            readChannels,
            readDirects: [...readDirects, conversationFound],
            unreadChats: newUnreadChats.filter(
              (_, index) => index !== conversationIndex
            ),
          });
        }
      }
      //if we are using a mobile app, set this state to to true
      if (window.innerWidth < 640) {
        setMobileChatOpened(true);
      }
    };

    /**
     * Sort a conversation
     * @param {*} sortType
     */
    const handleSortConversations = (sortType) => {
      let sortedConversations = { ...conversations };
      if (sortType === "sort-ascending") {
        sortedConversations = sortConversationsByName(conversations, "asc");
      } else if (sortType === "sort-descending") {
        sortedConversations = sortConversationsByName(conversations, "desc");
      }
      //(by recent)
      else {
        sortedConversations = sortByMostRecent(conversations);
      }
      setConversations(sortedConversations);
    };

    /**
     * Open the channel and message dropdown in the correct position
     * @param {*} e
     * @param {*} state
     * @param {*} value
     * @param {*} ref
     */
    const handleOpenDropdown = (e, state, value, ref) => {
      e.stopPropagation();
      e.preventDefault();
      if (ref?.current) {
        const rect = ref.current.getBoundingClientRect();
        setDropdownPosition(rect.bottom + window.scrollY - 20);
        setDropdownState({
          ...dropdownState,
          [state]: value,
        });
      }
    };

    /**
     * UseEffect to request the conversations, this call would be called everytime the user close the modals component(edit chat, new message and new channel)
     * And we are avoiding call it when the user starts the component and open the modal, this would be called only when the user closes the modal
     */
    useEffect(() => {
      const fetchData = async () => {
        if (prevModalType.current !== undefined && modalType === undefined) {
          const data = await getCommunityChatConversations(api, userId);
          setConversations(data);
        }
        prevModalType.current = modalType; // update the previous value
      };

      fetchData();
    }, [modalType]);

    /**
     * Create the refs of the chats to get the position and open the dropdown in the exact position
     * @param {*} id
     * @returns
     */
    const getRef = (id) => {
      if (!chatRefs.current[id]) {
        chatRefs.current[id] = React.createRef();
      }
      return chatRefs.current[id];
    };

    return (
      <div className={`chats-list-container`}>
        <div className="search-container">
          <div className="div-search-conversation-input div-search-conversation-input-width">
            <input
              className="input-search-chat-conversations input-search-chat-conversations-width"
              placeholder={t("communityChat.searchPlaceholder")}
              value={searchValue}
              type="search"
              onChange={({ target: { value } }) => setSearchValue(value)}
            />
          </div>
          <div>
            <button
              ref={newButtonRef}
              className="add-group-chat"
              onClick={(e) => {
                openDropdown(e, "newDropdown", !newDropdown);
              }}
              data-button-type="primary"
              type="button"
            >
              <span>{t("communityChat.new")}</span>

              <img
                src={
                  newDropdown
                    ? "/img/svg/angle-up.svg"
                    : "/img/svg/angle-down.svg"
                }
                alt={t("communityChat.newAlt")}
              />
            </button>
            {newDropdown && (
              <DropdownOptions
                options={newDropdownOptions}
                onSelectFn={handleOpenModal}
                addIcon={true}
                opened={newDropdown}
                initialDropwdownState={initialDropwdownState}
                setDropdownState={setDropdownState}
                excludeOutsideClickElements={[newButtonRef?.current]}
              />
            )}
          </div>

          <SettingsCommunityChatOptions
            displayState={displayState}
            webAppTheme={webAppTheme}
            directMessagesEnabled={directMessagesEnabled}
            onSort={handleSortConversations}
            onDisplay={(display) => {
              setDisplayState(display);
            }}
          />
        </div>
        <div className="main-chat-list-container">
          {unreadChats.length > 0 && (
            <div className="unreads-container">
              <div>
                <span>{t("communityChat.unread")}</span>
              </div>

              {filteredUnreadChats.map(
                ({
                  id,
                  avatar,
                  chatWithName,
                  subject,
                  unreadMessageCount,
                  custom,
                }) => {
                  const { isPush, isPress, isPerform, isPull, teamId } = custom;
                  const label = getConversationLabel(
                    isPush,
                    isPress,
                    isPerform,
                    isPull,
                    teamId
                  );
                  const labelColor = getConversationLabelColor(label);
                  const nameToShow = chatWithName ?? subject ?? "";
                  const unreadNumber =
                    unreadMessageCount > 0
                      ? Math.min(unreadMessageCount, 9)
                      : 0;
                  const imageToShow =
                    subject?.length > 0
                      ? "/img/new-icons/channel-white.svg"
                      : getAvatarUrl({ avatar }, false, webAppTheme);

                  return (
                    <div
                      className={
                        selectedConversation === id
                          ? "chat-card chat-card-selected"
                          : "chat-card"
                      }
                      key={id}
                    >
                      <button
                        className="info-chat-card"
                        onClick={() => handleSelectConversation(id)}
                        type="button"
                      >
                        <div className="image-name-chat-card">
                          <div className="img-channel-container">
                            <img
                              src={imageToShow}
                              alt={t("communityChat.avatarAlt")}
                              className={
                                subject?.length > 0
                                  ? "img-unread-channel"
                                  : "img-unread-chat"
                              }
                            />
                          </div>
                          <span className="info-chat-card-name">
                            {truncateText(nameToShow, 25)}
                          </span>
                          {label &&
                            (hasMultipleCommunities ||
                              (singleCommunityType === "push" &&
                                label === "TEAMS")) && (
                              <div
                                className="community-chat-label"
                                style={{
                                  border: `1px solid ${labelColor.color}`,
                                  color: labelColor.color,
                                  backgroundColor: labelColor.background,
                                }}
                              >
                                {label}
                              </div>
                            )}
                        </div>
                        <div className="unread-number text-white">
                          {unreadNumber}
                        </div>
                      </button>
                      <div className="actions-chat-card"></div>
                    </div>
                  );
                }
              )}
            </div>
          )}
          {readChannels.length === 0 ? (
            <div className="new-channels-container">
              {t("communityChat.channels")}
              {singleCommunityType &&
                ` ${singleCommunityType.toUpperCase()} (${
                  labels[singleCommunityType]
                })`}
              <div>
                <button
                  data-button-type="primary"
                  onClick={() => {
                    handleOpenModal("new-community-channel");
                  }}
                  type="button"
                >
                  <img
                    src="/img/new-icons/add.svg"
                    alt={t("communityChat.newAlt")}
                  />
                </button>
                <span>{t("communityChat.addNewChannelButton")}</span>
              </div>
            </div>
          ) : (
            displayState !== "show-messages" && (
              <div className="channels-container">
                <div className="community-identifier">
                  {t("communityChat.channels")}
                  {singleCommunityType &&
                    ` ${singleCommunityType.toUpperCase()} (${
                      labels[singleCommunityType]
                    })`}
                  <button
                    onClick={() => {
                      handleOpenModal("new-community-channel");
                    }}
                    type="button"
                  >
                    <img
                      src="/img/new-icons/add.svg"
                      alt={t("communityChat.newChannelAlt")}
                      className={
                        webAppTheme === "light"
                          ? "filter-black"
                          : "filter-white"
                      }
                    />
                  </button>
                </div>
                {filteredReadChannels.map((channel) => {
                  const { id, subject = "", notify = true, custom } = channel;
                  const {
                    isPush,
                    isPress,
                    isPerform,
                    isPull,
                    teamId,
                    readOnly = false,
                  } = custom;

                  const label = getConversationLabel(
                    isPush,
                    isPress,
                    isPerform,
                    isPull,
                    teamId
                  );
                  const labelColor = getConversationLabelColor(label);

                  return (
                    <div
                      className={
                        selectedConversation === id
                          ? "chat-card chat-card-selected"
                          : "chat-card"
                      }
                      key={id}
                      ref={getRef(id)}
                    >
                      <button
                        className="info-chat-card"
                        onClick={() => handleSelectConversation(id)}
                        type="button"
                      >
                        <div className="image-name-chat-card">
                          <div className="img-channel-container">
                            <SvgIcon
                              src={`/img/new-icons/${readOnly ? "megaphone.svg" : "channel.svg"}`}
                              classNames={readOnly ? "megaphone" : ""}
                              alt={t("communityChat.avatarAlt")}
                            />
                          </div>
                          <span>{truncateText(subject, 25)}</span>
                          {label &&
                            (hasMultipleCommunities ||
                              (singleCommunityType === "push" &&
                                label === "TEAMS")) && (
                              <div
                                className="community-chat-label"
                                style={{
                                  border: `1px solid ${labelColor.color}`,
                                  color: labelColor.color,
                                  backgroundColor: labelColor.background,
                                }}
                              >
                                {label}
                              </div>
                            )}
                        </div>
                        {!notify && (
                          <img
                            className={
                              webAppTheme === "light"
                                ? "filter-black"
                                : "filter-white"
                            }
                            src="/img/new-icons/volume-slash.svg"
                            alt={t("communityChat.mutedAlt")}
                          />
                        )}
                      </button>
                      <div className="actions-chat-card">
                        <button
                          className="button-conversations-actions"
                          onClick={(e) =>
                            handleOpenDropdown(
                              e,
                              "channelDropdown",
                              id,
                              getRef(id)
                            )
                          }
                          type="button"
                        >
                          <img
                            src="/img/new-icons/more-vertical.svg"
                            alt={t("communityChat.menuAlt")}
                            className={
                              webAppTheme === "light"
                                ? "filter-black"
                                : "filter-white"
                            }
                          />
                        </button>
                        {channelDropdown === id && (
                          <DropdownOptions
                            options={getDropdownOptions("channel", notify)}
                            onSelectFn={(value) =>
                              handleConversationAction(value, channel)
                            }
                            opened={!!channelDropdown.length}
                            initialDropwdownState={initialDropwdownState}
                            setDropdownState={setDropdownState}
                            excludeOutsideClickElements={[]}
                            dropdownPosition={dropdownPosition}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
          {directMessagesEnabled && displayState !== "show-channels" && (
            <div className="messages-container">
              <div className="community-identifier">
                <span>{t("communityChat.directMessages")}</span>
                <button
                  onClick={() => {
                    handleOpenModal("new-community-message");
                  }}
                  type="button"
                >
                  <img
                    src="/img/new-icons/add.svg"
                    alt={t("communityChat.directMessageAlt")}
                    className={
                      webAppTheme === "light" ? "filter-black" : "filter-white"
                    }
                  />
                </button>
              </div>
              {filteredReadDirects.map((chat) => {
                const {
                  id,
                  avatar,
                  chatWithName,
                  notify = true,
                  custom,
                } = chat;
                const { isPush, isPress, isPerform, isPull, teamId } = custom;
                const label = getConversationLabel(
                  isPush,
                  isPress,
                  isPerform,
                  isPull,
                  teamId
                );
                const labelColor = getConversationLabelColor(label);
                return (
                  <div
                    className={
                      selectedConversation === id
                        ? "chat-card chat-card-selected"
                        : "chat-card"
                    }
                    ref={getRef(id)}
                    key={id}
                  >
                    <button
                      className="info-chat-card"
                      onClick={() => handleSelectConversation(id)}
                      type="button"
                    >
                      <div className="image-name-chat-card">
                        <img
                          src={getAvatarUrl({ avatar }, false, webAppTheme)}
                          alt={t("communityChat.avatarAlt")}
                        />
                        <span>{truncateText(chatWithName, 25)}</span>
                        {label && hasMultipleCommunities && (
                          <div
                            className="community-chat-label"
                            style={{
                              border: `1px solid ${labelColor.color}`,
                              color: labelColor.color,
                              backgroundColor: labelColor.background,
                            }}
                          >
                            {label}
                          </div>
                        )}
                      </div>
                      {!notify && (
                        <img
                          className={
                            webAppTheme === "light"
                              ? "filter-black"
                              : "filter-white"
                          }
                          src="/img/new-icons/volume-slash.svg"
                          alt={t("communityChat.mutedAlt")}
                        />
                      )}
                    </button>
                    <div className="actions-chat-card">
                      <button
                        className="button-conversations-actions"
                        onClick={(e) =>
                          handleOpenDropdown(
                            e,
                            "messageDropdown",
                            id,
                            getRef(id)
                          )
                        }
                        type="button"
                      >
                        <img
                          src="/img/new-icons/more-vertical.svg"
                          alt={t("communityChat.menuAlt")}
                          className={
                            webAppTheme === "light"
                              ? "filter-black"
                              : "filter-white"
                          }
                        />
                      </button>
                      {messageDropdown === id && (
                        <DropdownOptions
                          options={getDropdownOptions("conversation", notify)}
                          onSelectFn={(value) =>
                            handleConversationAction(value, chat)
                          }
                          opened={!!messageDropdown.length}
                          initialDropwdownState={initialDropwdownState}
                          setDropdownState={setDropdownState}
                          excludeOutsideClickElements={[]}
                          dropdownPosition={dropdownPosition}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
);

CommunityChatList.propTypes = {
  locals: PropTypes.object,
  api: PropTypes.any,
  chatbox: PropTypes.any,
  conversations: PropTypes.object,
  selectedConversation: PropTypes.string,
  setConversations: PropTypes.func,
  setSelectedConversation: PropTypes.func,
  setMobileChatOpened: PropTypes.func,
};
